import { ArrowRight } from 'lucide-react';
import { Shamsa } from '@/components/persian-ornament';
import { CONTACT_EMAIL, PORTAL_URL } from '@/lib/config';
import { t, useLang, type StringTable } from '@/lib/i18n';

const STRINGS = {
  'contact.eyebrow': { en: 'Request access', fa: 'درخواست دسترسی' },
  'contact.title.lead': {
    en: 'See the same world,',
    fa: 'همان جهان را ببینید،',
  },
  'contact.title.accent': { en: 'with provenance', fa: 'با سندیّت' },
  'contact.title.tail': { en: '.', fa: '.' },
  'contact.subtitle': {
    en: 'We are onboarding a small cohort of analyst teams for the v0.1 preview. Tell us what you work on and we will open a seat.',
    fa: 'گروهِ کوچکی از تیم‌های تحلیلی را برای پیش‌نمایشِ نسخهٔ ۰٫۱ پذیرش می‌کنیم. بنویسید روی چه کار می‌کنید تا یک صندلی بگشاییم.',
  },
  'contact.cta': { en: 'Request access', fa: 'درخواست دسترسی' },
  'contact.enterprise': {
    en: 'For enterprise, self-hosted, or partnership inquiries, write to',
    fa: 'برای پرسش‌های سازمانی، خود-میزبان یا مشارکت، بنویسید به',
  },
} satisfies StringTable<string>;

export function Contact() {
  const lang = useLang();

  return (
    <section
      id="contact"
      className="vellum-grain relative overflow-hidden border-t border-[var(--color-border)] bg-[var(--color-surface)]"
    >
      <Shamsa
        size={720}
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[var(--color-accent)] opacity-[0.14]"
      />
      <div className="relative mx-auto max-w-4xl px-6 py-24 text-center md:py-32">
        <div className="mb-4 text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
          {t(STRINGS, 'contact.eyebrow', lang)}
        </div>
        <h2 className="text-3xl leading-tight md:text-5xl">
          {t(STRINGS, 'contact.title.lead', lang)}{' '}
          <span className="text-[var(--color-accent)]">
            {t(STRINGS, 'contact.title.accent', lang)}
          </span>
          {t(STRINGS, 'contact.title.tail', lang)}
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-[var(--color-ink-secondary)] md:text-lg">
          {t(STRINGS, 'contact.subtitle', lang)}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`${PORTAL_URL}/sign-up`}
            target="_self"
            className="inline-flex items-center gap-2 rounded-sm bg-[var(--color-ink)] px-6 py-3 text-sm font-medium text-[var(--color-canvas)] transition-colors hover:bg-[var(--color-accent)]"
          >
            {t(STRINGS, 'contact.cta', lang)}
            <ArrowRight className="icon-directional size-3.5" />
          </a>
        </div>

        <p className="mt-6 text-xs text-[var(--color-ink-muted)]">
          {t(STRINGS, 'contact.enterprise', lang)}{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="font-mono underline decoration-[var(--color-accent)] underline-offset-4 hover:text-[var(--color-ink)]"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </div>
    </section>
  );
}
